// frontend/src/components/dashboard/pos/styles/posStyles.js

// Enhanced gradient backgrounds with better contrast
export const gradients = {
  primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  primaryHover: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
  primaryLight: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  success: 'linear-gradient(135deg, #f0fff4 0%, #dcfce7 100%)',
  successSolid: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  warning: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  error: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
  info: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
  neutral: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
};

// Enhanced responsive breakpoints - refined for better tablet handling
export const breakpoints = {
  phone: {
    xs: 0,
    sm: 430,    // iPhone 14 Pro Max width
    md: 667     // Largest phone in portrait
  },
  tablet: {
    sm: 668,    // Just above largest phone
    md: 834,    // iPad Mini
    lg: 1080,   // iPad Air
    xl: 1366    // iPad Pro 12.9"
  },
  desktop: {
    sm: 1367,   // Just above largest tablet
    md: 1600,
    lg: 1920,
    xl: 2560
  }
};

// Device-specific configurations with enhanced tablet sizing
export const deviceConfigs = {
  phone: {
    cartWidth: '100%',
    categoriesWidth: 0,
    fontSize: { small: '0.75rem', medium: '0.85rem', large: '0.9rem' },
    spacing: 1,
    buttonHeight: 40,
    avatarSize: 32,
    cartItemHeight: 60,
    paymentButtonHeight: 70
  },
  tabletSmall: { // iPad Mini - Enhanced sizing
    cartWidth: 280,
    categoriesWidth: 80,
    fontSize: { small: '0.7rem', medium: '0.8rem', large: '0.9rem' },
    spacing: 0.75,
    buttonHeight: 36,
    avatarSize: 28,
    cartItemHeight: 65, // INCREASED from 45 to 65
    paymentButtonHeight: 50 // DECREASED from 55 to 50
  },
  tabletMedium: { // iPad Air - Enhanced sizing
    cartWidth: 320,
    categoriesWidth: 90,
    fontSize: { small: '0.75rem', medium: '0.85rem', large: '0.95rem' },
    spacing: 1,
    buttonHeight: 40,
    avatarSize: 32,
    cartItemHeight: 70, // INCREASED from 50 to 70
    paymentButtonHeight: 55 // DECREASED from 65 to 55
  },
  tabletLarge: { // iPad Pro - Enhanced sizing
    cartWidth: 360,
    categoriesWidth: 100,
    fontSize: { small: '0.8rem', medium: '0.9rem', large: '1rem' },
    spacing: 1.25,
    buttonHeight: 44,
    avatarSize: 36,
    cartItemHeight: 75, // INCREASED from 60 to 75
    paymentButtonHeight: 60 // DECREASED from 75 to 60
  },
  desktop: {
    cartWidth: 380,
    categoriesWidth: 120,
    fontSize: { small: '0.9rem', medium: '1rem', large: '1.1rem' },
    spacing: 1.5,
    buttonHeight: 48,
    avatarSize: 40,
    cartItemHeight: 80,
    paymentButtonHeight: 80
  }
};

// Common styles with enhanced tablet responsiveness
export const commonStyles = {
  // Main container styles
  fixedHeightContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    bgcolor: 'background.default'
  },

  mainContentContainer: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative'
  },

  // Enhanced card styling with tablet-responsive dimensions
  roundedCard: {
    borderRadius: { 
      xs: 1.5,  // Phone
      sm: 1.5,  // Small tablet
      md: 2,    // Medium tablet  
      lg: 2.5   // Large tablet/desktop
    },
    overflow: 'hidden',
    boxShadow: {
      xs: '0 2px 8px rgba(0,0,0,0.04)',
      sm: '0 3px 10px rgba(0,0,0,0.05)',
      md: '0 4px 12px rgba(0,0,0,0.05)'
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    backgroundColor: 'background.paper',
    '&:hover': {
      boxShadow: {
        xs: '0 4px 16px rgba(0,0,0,0.08)',
        sm: '0 5px 18px rgba(0,0,0,0.1)',
        md: '0 8px 24px rgba(0,0,0,0.12)'
      },
      transform: {
        xs: 'translateY(-1px)',
        sm: 'translateY(-1.5px)',
        md: 'translateY(-2px)'
      }
    },
    '&:active': {
      transform: 'translateY(0px)',
      boxShadow: {
        xs: '0 2px 8px rgba(0,0,0,0.04)',
        sm: '0 3px 10px rgba(0,0,0,0.05)'
      }
    }
  },
  
  // Enhanced header section with tablet-responsive sizing
  headerSection: {
    p: { 
      xs: 1.5,    // Phone
      sm: 1.25,   // Small tablet (reduced)
      md: 1.5,    // Medium tablet
      lg: 2       // Large tablet/desktop
    }, 
    background: gradients.primary,
    color: 'white',
    borderRadius: { xs: '12px 0 0 0', sm: '16px 0 0 0' },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    minHeight: { 
      xs: 56,     // Phone
      sm: 48,     // Small tablet (reduced)
      md: 56,     // Medium tablet
      lg: 64      // Large tablet/desktop
    },
    '& .MuiTypography-root': {
      fontSize: {
        xs: '1rem',     // Phone
        sm: '0.9rem',   // Small tablet (reduced)
        md: '1rem',     // Medium tablet
        lg: '1.1rem',   // Large tablet
        xl: '1.25rem'   // Desktop
      }
    }
  },
  
  // Enhanced cart item styling with proper tablet heights
  cartItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: { 
      xs: 1.25,   // Phone
      sm: 1,      // Small tablet (reduced)
      md: 1.25,   // Medium tablet
      lg: 1.5     // Large tablet/desktop
    },
    borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
    transition: 'background-color 0.2s ease',
    minHeight: { 
      xs: 60,     // Phone
      sm: 65,     // Small tablet - INCREASED
      md: 70,     // Medium tablet - INCREASED
      lg: 75      // Large tablet - INCREASED
    },
    '&:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.05)'
    },
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  
  // Enhanced quantity button with tablet-responsive sizing
  quantityButton: {
    color: '#667eea',
    background: 'rgba(102, 126, 234, 0.1)',
    width: { 
      xs: 28,     // Phone
      sm: 24,     // Small tablet (reduced)
      md: 28,     // Medium tablet
      lg: 32      // Large tablet/desktop
    },
    height: { 
      xs: 28,     // Phone
      sm: 24,     // Small tablet (reduced)
      md: 28,     // Medium tablet
      lg: 32      // Large tablet/desktop
    },
    minWidth: { 
      xs: 28,     // Phone
      sm: 24,     // Small tablet (reduced)
      md: 28,     // Medium tablet
      lg: 32      // Large tablet/desktop
    },
    borderRadius: { xs: '6px', sm: '6px', md: '8px' },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: 'rgba(102, 126, 234, 0.2)',
      transform: 'scale(1.05)'
    },
    '&:active': {
      transform: 'scale(0.95)'
    },
    '&:disabled': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'rgba(0, 0, 0, 0.26)',
      transform: 'none'
    }
  },
  
  // Enhanced payment method button with tablet-specific sizing
  paymentMethodButton: (selected, deviceType = 'desktop', screenSize = 'large') => {
    const config = getDeviceConfig(deviceType, screenSize);
    const buttonHeight = config.paymentButtonHeight;
    
    return {
      minHeight: `${buttonHeight}px`,
      width: '100%',
      aspectRatio: '1',
      borderRadius: { 
        xs: '10px', 
        sm: '8px',    // Small tablet (reduced)
        md: '10px',   // Medium tablet
        lg: '12px'    // Large tablet/desktop
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: { 
        xs: 0.5, 
        sm: 0.25,     // Small tablet (reduced)
        md: 0.5, 
        lg: 1 
      },
      p: { 
        xs: 0.75, 
        sm: 0.5,      // Small tablet (reduced)
        md: 0.75, 
        lg: 1 
      },
      background: selected
        ? gradients.primary
        : 'rgba(102, 126, 234, 0.08)',
      color: selected ? 'white' : '#667eea',
      border: selected 
        ? '2px solid transparent'
        : '1px solid rgba(102, 126, 234, 0.2)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      textTransform: 'none',
      '&:hover:not(:disabled)': {
        background: selected
          ? gradients.primaryHover
          : 'rgba(102, 126, 234, 0.15)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
        borderColor: selected 
          ? 'transparent'
          : 'rgba(102, 126, 234, 0.4)'
      },
      '&:active:not(:disabled)': {
        transform: 'translateY(0px)',
        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.15)'
      },
      '&:disabled': {
        background: 'rgba(0, 0, 0, 0.04)',
        color: 'rgba(0, 0, 0, 0.26)',
        transform: 'none',
        boxShadow: 'none',
        cursor: 'not-allowed',
        border: '1px solid rgba(0, 0, 0, 0.12)'
      },
      // Responsive text sizing with tablet optimization
      '& .MuiTypography-root': {
        fontSize: {
          xs: '0.65rem',    // Phone
          sm: '0.55rem',    // Small tablet (reduced)
          md: '0.65rem',    // Medium tablet  
          lg: '0.7rem',     // Large tablet
          xl: '0.75rem'     // Desktop
        },
        fontWeight: 500,
        textAlign: 'center',
        lineHeight: 1.2
      }
    };
  },
  
  // Enhanced mobile cart button - CENTERED positioning
  mobileCartButton: {
    borderRadius: { xs: 20, sm: 24 },
    p: { xs: 1.5, sm: 2 },
    minWidth: 'auto',
    background: gradients.primary,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: { xs: '0.9rem', sm: '1rem' },
    fontWeight: 600,
    color: 'white',
    // CENTERED POSITIONING
    position: 'fixed',
    bottom: { xs: 20, sm: 24 },
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    '&:hover:not(:disabled)': {
      background: gradients.primaryHover,
      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
      transform: 'translateX(-50%) translateY(-2px)'
    },
    '&:active:not(:disabled)': {
      transform: 'translateX(-50%) translateY(0px)'
    },
    '&:disabled': {
      background: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
      transform: 'translateX(-50%)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }
  },

  // Enhanced scrollbar with tablet-optimized sizing
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: { 
        xs: '4px',      // Phone
        sm: '3px',      // Small tablet (reduced)
        md: '4px',      // Medium tablet
        lg: '6px'       // Large tablet/desktop
      },
      height: { 
        xs: '4px', 
        sm: '3px', 
        md: '4px', 
        lg: '6px' 
      }
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(102, 126, 234, 0.2)',
      borderRadius: '10px',
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(102, 126, 234, 0.3)'
      }
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent'
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(102, 126, 234, 0.2) transparent'
  },
  
  // Enhanced category styles with tablet responsiveness
  categoryContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    overflow: 'hidden'
  },

  categoryScrollButton: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    bgcolor: 'background.paper',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': { 
      bgcolor: 'action.hover',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateX(-50%) scale(1.1)'
    },
    width: { 
      xs: 28, 
      sm: 24,     // Small tablet (reduced)
      md: 28, 
      lg: 32 
    },
    height: { 
      xs: 28, 
      sm: 24,     // Small tablet (reduced)
      md: 28, 
      lg: 32 
    },
    borderRadius: '50%'
  },

  categoryListItem: {
    borderRadius: { 
      xs: 1.5, 
      sm: 1,      // Small tablet (reduced)
      md: 1.5, 
      lg: 2 
    }, 
    mb: { 
      xs: 0.5, 
      sm: 0.25,   // Small tablet (reduced)
      md: 0.5, 
      lg: 1 
    },
    flexDirection: 'column',
    p: { 
      xs: 0.75, 
      sm: 0.5,    // Small tablet (reduced)
      md: 0.75, 
      lg: 1 
    },
    cursor: 'pointer',
    minHeight: { 
      xs: 70, 
      sm: 55,     // Small tablet (reduced)
      md: 65, 
      lg: 80 
    },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.08)',
      transform: 'translateY(-1px)'
    },
    '&:active': {
      transform: 'translateY(0px)'
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(102, 126, 234, 0.15)',
      '&:hover': {
        backgroundColor: 'rgba(102, 126, 234, 0.2)'
      }
    }
  },

  categoryAvatar: (isSelected, colorValue) => ({
    bgcolor: isSelected 
      ? 'primary.main' 
      : (colorValue || 'action.selected'),
    width: { 
      xs: 32, 
      sm: 26,     // Small tablet (reduced)
      md: 30, 
      lg: 36 
    },
    height: { 
      xs: 32, 
      sm: 26,     // Small tablet (reduced)
      md: 30, 
      lg: 36 
    },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  }),

  // Enhanced form controls with tablet scaling
  formControl: {
    '& .MuiOutlinedInput-root': {
      borderRadius: { 
        xs: '8px', 
        sm: '6px',    // Small tablet (reduced)
        md: '8px', 
        lg: '10px' 
      },
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      fontSize: { 
        xs: '0.9rem', 
        sm: '0.8rem',   // Small tablet (reduced)
        md: '0.9rem', 
        lg: '1rem' 
      },
      '&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
      },
      '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
        borderWidth: '2px'
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f44336',
        borderWidth: '2px'
      },
      '& input': {
        py: { 
          xs: 1, 
          sm: 0.75,    // Small tablet (reduced)
          md: 1, 
          lg: 1.25 
        }
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: { 
        xs: '0.7rem', 
        sm: '0.65rem',  // Small tablet (reduced)
        md: '0.7rem', 
        lg: '0.75rem' 
      },
      marginTop: '4px'
    }
  },

  // Enhanced animation presets
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { 
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  },

  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: { 
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  },

  scaleIn: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.8, opacity: 0 },
    transition: { 
      type: "spring", 
      stiffness: 200, 
      damping: 20,
      delay: 0.1 
    }
  },

  // Enhanced error and loading states
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: { xs: 2, sm: 1.5, md: 2, lg: 3 },
    textAlign: 'center',
    minHeight: { xs: 180, sm: 150, md: 180, lg: 200 },
    '& .error-icon': {
      fontSize: { 
        xs: '3rem', 
        sm: '2.5rem',   // Small tablet (reduced)
        md: '3rem', 
        lg: '4rem' 
      },
      opacity: 0.6,
      mb: 2,
      color: 'error.main'
    },
    '& .error-title': {
      fontWeight: 600,
      mb: 1,
      fontSize: { 
        xs: '1.1rem', 
        sm: '1rem',     // Small tablet (reduced)
        md: '1.1rem', 
        lg: '1.25rem' 
      }
    },
    '& .error-description': {
      opacity: 0.8,
      mb: 2,
      fontSize: { 
        xs: '0.9rem', 
        sm: '0.8rem',   // Small tablet (reduced)
        md: '0.9rem', 
        lg: '1rem' 
      }
    }
  },

  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: { xs: 180, sm: 150, md: 180, lg: 200 },
    flexDirection: 'column',
    gap: 2
  }
};

// Enhanced payment methods grid with tablet-responsive behavior
export const paymentMethodsGrid = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',   // 2 columns on mobile
    sm: 'repeat(3, 1fr)',   // 3 columns on all tablets and up
  },
  gap: { 
    xs: 0.75, 
    sm: 0.5,      // Small tablet (reduced)
    md: 0.75, 
    lg: 1 
  },
  mb: { 
    xs: 1.5, 
    sm: 1,        // Small tablet (reduced)
    md: 1.5, 
    lg: 2 
  },
  // Ensure all grid items have equal height with enhanced tablet scaling
  '& > *': {
    minHeight: { 
      xs: '70px', 
      sm: '50px',   // Small tablet (reduced)
      md: '55px',   // Medium tablet (reduced) 
      lg: '60px',   // Large tablet (reduced)
      xl: '80px'    // Desktop 
    },
    aspectRatio: '1'
  }
};

// Utility function to get device-specific config
export const getDeviceConfig = (deviceType, screenSize) => {
  if (deviceType === 'mobile') {
    return deviceConfigs.phone;
  } else if (deviceType === 'tablet') {
    switch (screenSize) {
      case 'small': return deviceConfigs.tabletSmall;
      case 'medium': return deviceConfigs.tabletMedium;
      case 'large': return deviceConfigs.tabletLarge;
      default: return deviceConfigs.tabletMedium;
    }
  } else {
    return deviceConfigs.desktop;
  }
};

// Export everything else as before...
export const colors = {
  primary: {
    main: '#667eea',
    light: '#9aa7f0',
    dark: '#4c63d2',
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#764ba2',
    light: '#9575cd',
    dark: '#512da8',
    contrastText: '#ffffff'
  },
  success: {
    main: '#10b981',
    light: '#34d399',
    dark: '#059669',
    contrastText: '#ffffff'
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
    contrastText: '#ffffff'
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
    contrastText: '#000000'
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    contrastText: '#ffffff'
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717'
  }
};

export const shadows = {
  card: {
    xs: '0 2px 8px rgba(0,0,0,0.04)',
    sm: '0 3px 10px rgba(0,0,0,0.05)',
    md: '0 4px 12px rgba(0,0,0,0.05)'
  },
  cardHover: {
    xs: '0 4px 16px rgba(0,0,0,0.08)',
    sm: '0 5px 18px rgba(0,0,0,0.1)',
    md: '0 8px 24px rgba(0,0,0,0.12)'
  },
  cardActive: {
    xs: '0 2px 8px rgba(0,0,0,0.06)',
    sm: '0 2px 8px rgba(0,0,0,0.08)'
  },
  button: '0 2px 8px rgba(102, 126, 234, 0.2)',
  buttonHover: '0 4px 12px rgba(102, 126, 234, 0.3)',
  buttonActive: '0 1px 4px rgba(102, 126, 234, 0.25)',
  dialog: '0 8px 32px rgba(0, 0, 0, 0.08)',
  drawer: '0 8px 32px rgba(0, 0, 0, 0.08)',
  dropdown: '0 4px 16px rgba(0, 0, 0, 0.1)',
  tooltip: '0 2px 8px rgba(0, 0, 0, 0.15)'
};

export const validation = {
  deviceNumber: {
    pattern: /^[a-zA-Z0-9]+$/,
    maxLength: 10,
    minLength: 1
  },
  price: {
    pattern: /^\d+(\.\d{1,2})?$/,
    min: 0,
    max: 999999.99
  }
};

export const themeConfig = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    }
  },
  palette: colors,
  shadows: Object.values(shadows),
  spacing: 8
};